import { Calendar } from '@fullcalendar/core';
import dayGridMonth from '@fullcalendar/daygrid';
import timeGridWeek from '@fullcalendar/timegrid';
import interaction from '@fullcalendar/interaction';
import sweetAlert from 'sweetalert';
import moment from 'moment-timezone';
import { Turbo } from '@hotwired/turbo-rails';
import FlashMessage from './flash_message';
import SidePanel from './sidepanel';
import { locale } from '../../calendar/locales';
import '@fullcalendar/core/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';

const EventsCalendar = {
  start() {
    const $calendar = $('.js-events-calendar');
    if (!$calendar.length) {
      return;
    }
    this.setupCalendar($calendar);
    this.setVisibilityForActionbar($calendar);
    this.watchActionbarClicks($calendar);
  },

  setVisibilityForActionbar($calendar) {
    const { orgId } = $calendar.data();
    const selectedView = window.localStorage.getItem(`fcEventsView-org-${orgId}`);
    this.hideOrDisable();
    if (selectedView === 'calendar') {
      $('.js-show-calendar').addClass('is-on').removeClass('is-off');
      $($('.js-show-calendar').data('hideOnClick')).addClass('is-hidden');
      $('.js-calendar-container').removeClass('is-hidden');
      $calendar.trigger('resize');
    } else if (selectedView === 'list') {
      $('.js-show-events-list').addClass('is-on').removeClass('is-off');
      $($('.js-show-events-list').data('showOnClick')).removeClass('is-hidden');
      $('.js-list').removeClass('is-hidden');
    } else {
      $('.js-show-events-tiles').addClass('is-on').removeClass('is-off');
      $($('.js-show-events-tiles').data('hideOnClick')).addClass('is-hidden');
      $('.js-tiles-container').removeClass('is-hidden');
    }
  },

  hideOrDisable() {
    const $actionbar = $('.js-show-events-tiles,.js-show-events-list,.js-show-calendar');
    $actionbar.addClass('is-off').removeClass('is-on');
    $($actionbar.data('hideOnClick')).addClass('is-hidden');
    $('.js-calendar-container,.js-list,.js-tiles-container').addClass('is-hidden');
  },

  watchActionbarClicks($calendar) {
    const { orgId } = $calendar.data();
    $('.js-show-calendar').on('click', () => {
      $calendar.trigger('resize');
      window.localStorage.setItem(`fcEventsView-org-${orgId}`, 'calendar');
    });
    $('.js-show-events-list').on('click', () => {
      window.localStorage.setItem(`fcEventsView-org-${orgId}`, 'list');
      if ($calendar.data('reloadListView')) {
        Turbo.visit(window.location, { action: 'replace' });
      }
    });
    $('.js-show-events-tiles').on('click', () => {
      window.localStorage.setItem(`fcEventsView-org-${orgId}`, 'tiles');
      if ($calendar.data('reloadListView')) {
        Turbo.visit(window.location, { action: 'replace' });
      }
    });
  },

  setupCalendar($calendar) {
    let search = '';
    let debounce = null;
    let duplicatedEvent = null;
    const {
      url, newEventUrl, offsetChangeUrl, duplicateUrl, updateUrl, orgId
    } = $calendar.data();
    const $modalTemplate = $('.js-modal-template');
    const $visibleModal = $('.js-visible-modal');
    const { lang } = document.documentElement;
    const calendar = new Calendar($calendar.get(0), {
      locale: locale(lang),
      plugins: [interaction, dayGridMonth, timeGridWeek],
      header: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek'
      },
      defaultDate: (window.localStorage.getItem(`fcDefaultDate-org-${orgId}`) || new Date()),
      defaultView: (window.localStorage.getItem(`fcDefaultView-org-${orgId}`) || 'dayGridMonth'),
      allDaySlot: false,
      selectable: true,
      editable: true,
      height: 'parent',
      width: 'parent',
      events: { url },
      unselectCancel: '.sidepanel',
      views: {
        week: {
          slotDuration: '01:00:00'
        }
      },
      datesRender(info) {
        const { view: { type } } = info;
        const { currentDate } = calendar.state;
        window.localStorage.setItem(`fcDefaultView-org-${orgId}`, type);
        window.localStorage.setItem(`fcDefaultDate-org-${orgId}`, currentDate.toISOString());
      },
      eventResize(info) {
        const { days, milliseconds } = info.endDelta;
        const seconds = milliseconds / 1000;
        const resizeUrl = updateUrl.replace('EVENT_ID', info.event.id);
        $.ajax({
          url: resizeUrl,
          data: { event: { end_date_time_offset: { seconds, days } } },
          dataType: 'json',
          type: 'PATCH',
          error(jqXHR) {
            const { responseText, statusText } = jqXHR;
            const errorMessage = (jqXHR.responseJSON && jqXHR.responseJSON.error)
              ? jqXHR.responseJSON.error : responseText;
            sweetAlert({ title: statusText, text: errorMessage });
            info.revert();
          }
        }).done(() => {
          $calendar.data({ reloadListView: true });
          FlashMessage.showMessageWithTimer('info', 'End time extended successfully.');
        });
      },
      eventDragStart(info) {
        if (info.jsEvent.altKey || info.jsEvent.ctrlKey) {
          duplicatedEvent = info.event.id;
          const eClone = {
            id: info.event.id,
            title: info.event.title,
            start: info.event.start,
            end: info.event.end
          };
          calendar.addEvent(eClone);
        }
      },
      eventDrop(info) {
        const { days, milliseconds } = info.delta;
        const seconds = milliseconds / 1000;
        if (duplicatedEvent) {
          $.ajax({
            url: duplicateUrl.replace('EVENT_ID', info.event.id),
            data: { event: { offset: { seconds, days } } },
            dataType: 'json',
            type: 'POST',
            error(jqXHR) {
              const { responseText, statusText } = jqXHR;
              const errorMessage = (jqXHR.responseJSON && jqXHR.responseJSON.error)
                ? jqXHR.responseJSON.error : responseText;
              sweetAlert({ title: statusText, text: errorMessage });
              info.revert();
            }
          }).done(() => {
            $calendar.data({ reloadListView: true });
            calendar.refetchEvents();
            calendar.removeAllEvents();
          });
          duplicatedEvent = null;
        } else {
          sweetAlert({
            title: 'Confirmation',
            text: `You are moving "${info.event.title}" and all its activities to a new start date/time. Please confirm.`,
            icon: 'warning',
            buttons: ['Cancel', 'Confirm']
          }).then((willSubmit) => {
            if (willSubmit) {
              const offsetUrl = offsetChangeUrl.replace('EVENT_ID', info.event.id);
              $calendar.data({ reloadListView: true });
              $.ajax({
                url: offsetUrl,
                data: { event: { offset: { seconds, days } } },
                dataType: 'json',
                type: 'PATCH',
                error(jqXHR) {
                  const { responseText, statusText } = jqXHR;
                  const errorMessage = (jqXHR.responseJSON && jqXHR.responseJSON.error)
                    ? jqXHR.responseJSON.error : responseText;
                  sweetAlert({ title: statusText, text: errorMessage });
                  info.revert();
                }
              });
            } else {
              info.revert();
            }
          });
        }
      },
      eventClick(info) {
        $(info.el).addClass('is-active');
        $visibleModal.empty();
        $modalTemplate.clone(true, true).appendTo($visibleModal);
        $visibleModal.find('a').each((_, el) => {
          el.setAttribute('href', el.href.replace('EVENT_ID', info.event.id));
        });
        const $modalWithConfirmation = $visibleModal.find('.js-replace-event-data-name');
        if ($modalWithConfirmation.length) {
          $modalWithConfirmation.attr(
            'data-text', $modalWithConfirmation.data('text').replace('EVENT_NAME', info.event.title)
          );
        }
        $visibleModal.find('.js-replace-event-name').text(info.event.title);
        const $modal = $visibleModal.find('.modal');
        $modal.toggleClass('is-invisible is-visible');
        $modal.find('.js-modal-close').on('click', () => {
          $(info.view.el).find('.fc-event').removeClass('is-active');
          $modal.toggleClass('is-invisible is-visible');
        });
      },
      eventRender(info) {
        return info.event.title.toLowerCase().indexOf(search) > -1;
      },
      select(info) {
        const { allDay, startStr, endStr } = info;
        let start = null;
        let end = null;
        if (allDay) {
          start = moment(startStr).hours(8);
          end = moment(endStr).subtract(1, 'second').hours(17).minutes(0)
            .seconds(0);
        } else {
          start = moment(startStr);
          end = moment(endStr).minutes(0).seconds(0);
        }
        const paramsData = {
          start: start.format('YYYY-MM-DD[T]HH:mm:ss'),
          end: end.format('YYYY-MM-DD[T]HH:mm:ss')
        };
        SidePanel.loadFromAjax({ url: newEventUrl, data: paramsData });
      }
    });

    $('.js-search-input').on('keyup change', (event) => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        search = $(event.target).val().toLowerCase();
        calendar.rerenderEvents();
      }, 400);
    });

    calendar.render();
  }
};

export default EventsCalendar;
